import { FC } from 'react';
import { cn } from '@new/lib';

interface LabeledNumberInputProps {
  label: string;
  value: number | string;
  onChange?: (value: number | null) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  hasError?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * A labeled numeric input using the compact "transcript" field styling
 * (48px tall, 8px radius, light border/background) shared across the transcript
 * flows. Supports a disabled read-only state. Emits `null` for an empty value.
 */
export const LabeledNumberInput: FC<LabeledNumberInputProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  disabled = false,
  hasError = false,
  placeholder,
  className,
}) => (
  <div className={cn('flex flex-col gap-2', className)}>
    <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
      {label}
    </label>
    <input
      type="number"
      min={min}
      max={max}
      disabled={disabled}
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        onChange?.(v === '' ? null : Number(v));
      }}
      className={cn(
        'h-[48px] w-full rounded-[8px]! border-[1.5px]! border-solid! px-4',
        '[font-family:var(--font-montserrat)]! [font-size:var(--fs-l4)]! text-(--neutral-black-NB1000)!',
        'bg-[#FCFDFD]! placeholder:text-(--neutral-white-NW600)!',
        'focus:outline-none! focus:border-(--neutral-black-NB1000)/80!',
        'disabled:cursor-not-allowed disabled:opacity-70',
        hasError ? 'border-(--danger-D700)/80!' : 'border-[#E1E7EA]!',
        '[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]'
      )}
    />
  </div>
);
